<script lang="ts">
  import InlineSVG from 'svelte-inline-svg';
  import {selectedProjectIds} from '../../stores/selectedProjects';
  import {projects} from '../../stores/projects';
  import {Visibility} from '../../types/project';
  import desktopIcon from '../../assets/icons/desktop.svg';
  import {showcaseUrl} from '../../constants/urls';

  // Check if any selected projects are non-public
  $: hasNonPublicProjects = $selectedProjectIds.some(id => {
    const project = $projects.get(id);
    return project && project.visibility !== Visibility.PUBLIC;
  });

  // Button should be disabled if user has selected projects AND any are non-public
  $: isDisabled = $selectedProjectIds.length > 0 && hasNonPublicProjects;

  // Count of selected public projects
  $: selectedPublicCount = $selectedProjectIds.filter(id => {
    const project = $projects.get(id);
    return project && project.visibility === Visibility.PUBLIC;
  }).length;

  // Dynamic button text based on selection
  $: buttonText =
    $selectedProjectIds.length > 0 && !hasNonPublicProjects
      ? `Open ${selectedPublicCount} Public Project${
          selectedPublicCount !== 1 ? 's' : ''
        } in Showcase`
      : 'Open Public Projects in Showcase';

  // Dynamic URL generation based on selection
  $: showcaseLink = (() => {
    const hashParams = new URLSearchParams();

    if ($selectedProjectIds.length > 0 && !hasNonPublicProjects) {
      hashParams.set('projects', $selectedProjectIds.join(','));
    }

    return `${showcaseUrl}/#/${hashParams.toString() ? `?${hashParams}` : ''}`;

  })();

  // Tooltip for disabled state
  $: tooltipText = isDisabled
    ? 'Deselect non-public projects to open in showcase'
    : '';

  function handleClick(event: MouseEvent) {
    if (isDisabled) {
      event.preventDefault();
    }
  }
</script>

<style lang="postcss">
  .showcase-link {
    display: flex;
    align-items: center;
    height: var(--ubilabs-link-button-height);
    padding: var(--ubilabs-link-button-padding);
    border: none;
    border-radius: var(--ubilabs-link-button-border-radius);
    font-size: var(--ubilabs-link-button-font-size);
    font-weight: var(--ubilabs-link-button-font-weight);
    line-height: var(--ubilabs-link-button-line-height);
    letter-spacing: var(--ubilabs-link-button-line-spacing);
    text-decoration: none;
    cursor: pointer;
    transition: all 0.2s ease;
    background-color: var(--ubilabs-link-button-background-color);
    color: var(--ubilabs-link-button-label-color);
  }

  @media (max-width: 767px) {
    .showcase-link span:last-child {
      display: none;
    }
  }

  .showcase-link:hover:not(.disabled) {
    background-color: var(--ubilabs-link-button-hover-background-color);
  }

  .showcase-link:active:not(.disabled) {
    background-color: var(--ubilabs-link-button-active-background-color);
  }

  .showcase-link:focus-visible {
    outline: 2px solid var(--ubilabs-blue);
    outline-offset: 2px;
  }

  .showcase-link.disabled {
    background-color: var(--ubilabs-link-button-disabled-background-color);
    color: var(--ubilabs-link-button-disabled-label-color);
    cursor: not-allowed;
  }

  .icon {
    display: inline-flex;
    align-items: center;
    margin: 0 0.5em 0 -0.5em;
  }

  .showcase-link span:not(.icon) {
    margin: 0 0.25rem;
  }
</style>

<a
  href={showcaseLink}
  target="_blank"
  rel="noopener noreferrer"
  class="showcase-link {isDisabled ? 'disabled' : ''}"
  aria-disabled={isDisabled}
  title={tooltipText}
  on:click={handleClick}
>
  <span class="icon"><InlineSVG src={desktopIcon} /></span>
  <span>{buttonText}</span>
</a>
